import api from './api';

export interface Comment {
  id: string;
  contentPageId: string;
  content: string;
  author: {
    id: string;
    name: string;
    fullName: string;
    avatar?: string;
  };
  parentId?: string | null;
  likes: number;
  isEdited: boolean;
  createdAt: string;
  updatedAt?: string;
  replies?: Comment[]; // For nested replies
  isLiked?: boolean;
}

export interface CreateCommentParams {
  contentPageId: string;
  content: string;
  parentId?: string | null;
}

export interface UpdateCommentParams {
  content: string;
}

export const commentService = {
  /**
   * Get comments for a content page
   */
  async getComments(contentPageId: string, parentId?: string | null): Promise<Comment[]> {
    const params = new URLSearchParams();
    params.append('contentPageId', contentPageId);
    if (parentId !== undefined) {
      params.append('parentId', parentId === null ? 'null' : parentId);
    }
    return api.get(`/comments?${params.toString()}`);
  },

  /**
   * Get comment by ID
   */
  async getById(id: string): Promise<Comment> {
    return api.get(`/comments/${id}`);
  },

  async getLikeStatuses(ids: string[]): Promise<Record<string, boolean>> {
    if (ids.length === 0) return {};
    return api.get(`/comments/likes/status?ids=${encodeURIComponent(ids.join(','))}`);
  },

  /**
   * Create a new comment
   */
  async create(params: CreateCommentParams): Promise<Comment> {
    return api.post('/comments', params);
  },

  /**
   * Update comment
   */
  async update(id: string, params: UpdateCommentParams): Promise<Comment> {
    return api.put(`/comments/${id}`, params);
  },

  /**
   * Delete comment
   */
  async delete(id: string): Promise<void> {
    return api.delete(`/comments/${id}`);
  },

  /**
   * Like a comment
   */
  async like(id: string): Promise<{ liked: true; likes: number }> {
    return api.post(`/comments/${id}/like`);
  },

  /**
   * Unlike a comment
   */
  async unlike(id: string): Promise<{ liked: false; likes: number }> {
    return api.post(`/comments/${id}/unlike`);
  },
};
